import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

interface Props {
  settings?: StoreSettings;
  storeId: string;
}

const FONT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'cairo', label: 'Cairo' },
  { value: 'tajawal', label: 'Tajawal' },
  { value: 'inter', label: 'Inter' },
  { value: 'poppins', label: 'Poppins' },
  { value: 'roboto', label: 'Roboto' },
];

export function ThemeTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const theme = settings?.theme ?? {};

  // Basic
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor ?? '#6366f1');
  const [secondaryColor, setSecondaryColor] = useState(theme.secondaryColor ?? '');
  const [accentColor, setAccentColor] = useState(theme.accentColor ?? '#8b5cf6');
  const [textColor, setTextColor] = useState(theme.textColor ?? '');
  const [backgroundColor, setBackgroundColor] = useState(theme.backgroundColor ?? '');
  const [fontFamily, setFontFamily] = useState(theme.fontFamily ?? '');
  const [customCss, setCustomCss] = useState(theme.customCss ?? '');

  // Extended
  const [buttonColor, setButtonColor] = useState(theme.buttonColor ?? '');
  const [buttonTextColor, setButtonTextColor] = useState(theme.buttonTextColor ?? '');
  const [headingFont, setHeadingFont] = useState(theme.headingFont ?? '');
  const [bodyFont, setBodyFont] = useState(theme.bodyFont ?? '');
  const [customFontUrl, setCustomFontUrl] = useState(theme.customFontUrl ?? '');
  const [headingTransform, setHeadingTransform] = useState(theme.headingTransform ?? '');

  // Button gradient (overrides buttonColor when both fields are set)
  const [gradientFrom, setGradientFrom] = useState(theme.gradientFrom ?? '');
  const [gradientTo, setGradientTo] = useState(theme.gradientTo ?? '');

  // Dark mode
  const [darkLogo, setDarkLogo] = useState(theme.darkLogo ?? '');
  const [darkPrimary, setDarkPrimary] = useState(theme.dark?.primaryColor ?? '');
  const [darkAccent, setDarkAccent] = useState(theme.dark?.accentColor ?? '');
  const [darkBg, setDarkBg] = useState(theme.dark?.backgroundColor ?? '');
  const [darkText, setDarkText] = useState(theme.dark?.textColor ?? '');
  const [darkButtonColor, setDarkButtonColor] = useState(theme.dark?.buttonColor ?? '');
  const [darkButtonTextColor, setDarkButtonTextColor] = useState(theme.dark?.buttonTextColor ?? '');
  const [defaultMode, setDefaultMode] = useState(theme.defaultMode ?? 'light');
  const [showToggle, setShowToggle] = useState(theme.showToggle ?? true);

  // Design tokens
  const [spacingScale, setSpacingScale] = useState(theme.spacingScale ?? 'default');
  const [contentWidth, setContentWidth] = useState(theme.contentWidth ?? 'default');
  const [globalRadius, setGlobalRadius] = useState(theme.globalRadius ?? 'soft');
  const [buttonRadius, setButtonRadius] = useState(theme.buttonRadius ?? 'soft');
  const [buttonStyle, setButtonStyle] = useState(theme.buttonStyle ?? 'solid');
  const [loaderStyle, setLoaderStyle] = useState(theme.loaderStyle ?? 'progress');
  const [defaultAnimation, setDefaultAnimation] = useState(theme.defaultAnimation ?? 'fade');
  const [defaultAnimateSpeed, setDefaultAnimateSpeed] = useState(theme.defaultAnimateSpeed ?? 'normal');

  useEffect(() => {
    const th = settings?.theme ?? {};
    setPrimaryColor(th.primaryColor ?? '#6366f1');
    setSecondaryColor(th.secondaryColor ?? '');
    setAccentColor(th.accentColor ?? '#8b5cf6');
    setTextColor(th.textColor ?? '');
    setBackgroundColor(th.backgroundColor ?? '');
    setFontFamily(th.fontFamily ?? '');
    setCustomCss(th.customCss ?? '');
    setButtonColor(th.buttonColor ?? '');
    setButtonTextColor(th.buttonTextColor ?? '');
    setGradientFrom(th.gradientFrom ?? '');
    setGradientTo(th.gradientTo ?? '');
    setHeadingFont(th.headingFont ?? '');
    setBodyFont(th.bodyFont ?? '');
    setCustomFontUrl(th.customFontUrl ?? '');
    setHeadingTransform(th.headingTransform ?? '');
    setDarkLogo(th.darkLogo ?? '');
    setDarkPrimary(th.dark?.primaryColor ?? '');
    setDarkAccent(th.dark?.accentColor ?? '');
    setDarkBg(th.dark?.backgroundColor ?? '');
    setDarkText(th.dark?.textColor ?? '');
    setDarkButtonColor(th.dark?.buttonColor ?? '');
    setDarkButtonTextColor(th.dark?.buttonTextColor ?? '');
    setDefaultMode(th.defaultMode ?? 'light');
    setShowToggle(th.showToggle ?? true);
    // Design tokens
    setSpacingScale(th.spacingScale ?? 'default');
    setContentWidth(th.contentWidth ?? 'default');
    setGlobalRadius(th.globalRadius ?? 'soft');
    setButtonRadius(th.buttonRadius ?? 'soft');
    setButtonStyle(th.buttonStyle ?? 'solid');
    setLoaderStyle(th.loaderStyle ?? 'progress');
    setDefaultAnimation(th.defaultAnimation ?? 'fade');
    setDefaultAnimateSpeed(th.defaultAnimateSpeed ?? 'normal');
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => settingsApi.updateTheme(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('settingsTheme.saveSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleSave = () => {
    const hasDarkColors = darkPrimary || darkAccent || darkBg || darkText || darkButtonColor || darkButtonTextColor;

    mutation.mutate({
      primaryColor, accentColor, fontFamily, customCss,
      secondaryColor: secondaryColor || undefined,
      textColor: textColor || undefined,
      backgroundColor: backgroundColor || undefined,
      buttonColor: buttonColor || undefined,
      buttonTextColor: buttonTextColor || undefined,
      gradientFrom: gradientFrom || undefined,
      gradientTo: gradientTo || undefined,
      headingFont: headingFont || undefined,
      bodyFont: bodyFont || undefined,
      customFontUrl: customFontUrl || undefined,
      headingTransform: headingTransform || undefined,
      darkLogo: darkLogo || undefined,
      defaultMode, showToggle,
      dark: hasDarkColors ? {
        primaryColor: darkPrimary || undefined,
        accentColor: darkAccent || undefined,
        backgroundColor: darkBg || undefined,
        textColor: darkText || undefined,
        buttonColor: darkButtonColor || undefined,
        buttonTextColor: darkButtonTextColor || undefined,
      } : undefined,
      // Design tokens
      spacingScale,
      contentWidth,
      globalRadius,
      buttonRadius,
      buttonStyle,
      loaderStyle,
      defaultAnimation,
      defaultAnimateSpeed,
    });
  };

  function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        <div className="flex items-center gap-2">
          <input type="color" className="h-10 w-14 rounded border border-border-default cursor-pointer" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} />
          <input className="flex-1 rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm outline-none focus:border-accent" value={value} onChange={(e) => onChange(e.target.value)} placeholder="#000000" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Colors */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTheme.title')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.primaryColor')} value={primaryColor} onChange={setPrimaryColor} />
          <ColorField label={t('settingsTheme.accentColor')} value={accentColor} onChange={setAccentColor} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.secondaryColor', 'Secondary Color')} value={secondaryColor} onChange={setSecondaryColor} />
          <ColorField label={t('settingsTheme.textColor', 'Text Color')} value={textColor} onChange={setTextColor} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.backgroundColor', 'Background')} value={backgroundColor} onChange={setBackgroundColor} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.buttonColor', 'Button Color')} value={buttonColor} onChange={setButtonColor} />
          <ColorField label={t('settingsTheme.buttonTextColor', 'Button Text Color')} value={buttonTextColor} onChange={setButtonTextColor} />
        </div>
        <div>
          <p className="text-xs text-text-muted mb-2">
            {t('settingsTheme.buttonGradientHint', 'Optional: a gradient that overrides the button color when both stops are set.')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <ColorField label={t('settingsTheme.gradientFrom', 'Gradient From')} value={gradientFrom} onChange={setGradientFrom} />
            <ColorField label={t('settingsTheme.gradientTo', 'Gradient To')} value={gradientTo} onChange={setGradientTo} />
          </div>
        </div>
      </section>

      {/* Typography */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTheme.typography', 'Typography')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label={t('settingsTheme.fontFamily')} options={FONT_OPTIONS} value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} />
          <Select label={t('settingsTheme.headingFont', 'Heading Font')} options={FONT_OPTIONS} value={headingFont} onChange={(e) => setHeadingFont(e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label={t('settingsTheme.bodyFont', 'Body Font')} options={FONT_OPTIONS} value={bodyFont} onChange={(e) => setBodyFont(e.target.value)} />
          <Select
            label={t('settingsTheme.headingTransform', 'Heading Transform')}
            options={[{ value: '', label: 'None' }, { value: 'uppercase', label: 'UPPERCASE' }, { value: 'capitalize', label: 'Capitalize' }]}
            value={headingTransform}
            onChange={(e) => setHeadingTransform(e.target.value)}
          />
        </div>
        <Input label={t('settingsTheme.customFontUrl', 'Custom Font URL')} placeholder="https://fonts.googleapis.com/css2?family=..." value={customFontUrl} onChange={(e) => setCustomFontUrl(e.target.value)} dir="ltr" />
      </section>

      {/* Dark Mode */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTheme.darkMode', 'Dark Mode')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('settingsTheme.defaultMode', 'Default Mode')}
            options={[{ value: 'light', label: t('common.lightMode') }, { value: 'dark', label: t('common.darkMode') }]}
            value={defaultMode}
            onChange={(e) => setDefaultMode(e.target.value)}
          />
          <div className="flex items-end">
            <Toggle label={t('settingsTheme.showModeToggle', 'Show Mode Toggle')} checked={showToggle} onChange={setShowToggle} />
          </div>
        </div>
        <Input label={t('settingsTheme.darkLogo', 'Dark Mode Logo URL')} value={darkLogo} onChange={(e) => setDarkLogo(e.target.value)} dir="ltr" />
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.darkPrimary', 'Dark Primary')} value={darkPrimary} onChange={setDarkPrimary} />
          <ColorField label={t('settingsTheme.darkAccent', 'Dark Accent')} value={darkAccent} onChange={setDarkAccent} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.darkBackground', 'Dark Background')} value={darkBg} onChange={setDarkBg} />
          <ColorField label={t('settingsTheme.darkText', 'Dark Text')} value={darkText} onChange={setDarkText} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ColorField label={t('settingsTheme.darkButtonColor', 'Dark Button Color')} value={darkButtonColor} onChange={setDarkButtonColor} />
          <ColorField label={t('settingsTheme.darkButtonTextColor', 'Dark Button Text Color')} value={darkButtonTextColor} onChange={setDarkButtonTextColor} />
        </div>
      </section>

      {/* Design Tokens */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTheme.designTokens', 'Advanced Customization')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('settingsTheme.spacingScale', 'Spacing Scale')}
            options={[
              { value: 'default', label: t('settingsTheme.spacingDefault', 'Default') },
              { value: 'spacious', label: t('settingsTheme.spacingSpacious', 'Spacious') },
              { value: 'compact', label: t('settingsTheme.spacingCompact', 'Compact') },
            ]}
            value={spacingScale}
            onChange={(e) => setSpacingScale(e.target.value)}
          />
          <Select
            label={t('settingsTheme.contentWidth', 'Content Width')}
            options={[
              { value: 'default', label: t('settingsTheme.widthDefault', 'Default') },
              { value: 'narrow', label: t('settingsTheme.widthNarrow', 'Narrow') },
              { value: 'wide', label: t('settingsTheme.widthWide', 'Wide') },
            ]}
            value={contentWidth}
            onChange={(e) => setContentWidth(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('settingsTheme.globalRadius', 'Corner Radius')}
            options={[
              { value: 'soft', label: t('settingsTheme.radiusSoft', 'Soft') },
              { value: 'rounded', label: t('settingsTheme.radiusRounded', 'Rounded') },
              { value: 'sharp', label: t('settingsTheme.radiusSharp', 'Sharp') },
            ]}
            value={globalRadius}
            onChange={(e) => setGlobalRadius(e.target.value)}
          />
          <Select
            label={t('settingsTheme.buttonRadius', 'Button Radius')}
            options={[
              { value: 'soft', label: t('settingsTheme.radiusSoft', 'Soft') },
              { value: 'rounded', label: t('settingsTheme.radiusRounded', 'Rounded') },
              { value: 'pill', label: t('settingsTheme.radiusPill', 'Pill') },
              { value: 'sharp', label: t('settingsTheme.radiusSharp', 'Sharp') },
            ]}
            value={buttonRadius}
            onChange={(e) => setButtonRadius(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('settingsTheme.buttonStyle', 'Button Style')}
            options={[
              { value: 'solid', label: t('settingsTheme.btnStyleSolid', 'Solid') },
              { value: 'soft', label: t('settingsTheme.btnStyleSoft', 'Soft') },
              { value: 'outline', label: t('settingsTheme.btnStyleOutline', 'Outline') },
            ]}
            value={buttonStyle}
            onChange={(e) => setButtonStyle(e.target.value)}
          />
          <Select
            label={t('settingsTheme.loaderStyle', 'Loader Style')}
            options={[
              { value: 'progress', label: t('settingsTheme.loaderProgress', 'Progress Bar') },
              { value: 'dots', label: t('settingsTheme.loaderDots', 'Dots') },
              { value: 'pulse', label: t('settingsTheme.loaderPulse', 'Pulse') },
              { value: 'glass', label: t('settingsTheme.loaderGlass', 'Glass') },
              { value: 'minimal', label: t('settingsTheme.loaderMinimal', 'Minimal') },
              { value: 'none', label: t('settingsTheme.loaderNone', 'None') },
            ]}
            value={loaderStyle}
            onChange={(e) => setLoaderStyle(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('settingsTheme.defaultAnimation', 'Animation')}
            options={[
              { value: 'fade', label: t('settingsTheme.animFade', 'Fade') },
              { value: 'fade-up', label: t('settingsTheme.animFadeUp', 'Fade Up') },
              { value: 'slide-left', label: t('settingsTheme.animSlideLeft', 'Slide Left') },
              { value: 'scale', label: t('settingsTheme.animScale', 'Scale') },
              { value: 'blur', label: t('settingsTheme.animBlur', 'Blur') },
              { value: 'none', label: t('settingsTheme.animNone', 'None') },
            ]}
            value={defaultAnimation}
            onChange={(e) => setDefaultAnimation(e.target.value)}
          />
          <Select
            label={t('settingsTheme.defaultAnimateSpeed', 'Animation Speed')}
            options={[
              { value: 'normal', label: t('settingsTheme.speedNormal', 'Normal') },
              { value: 'slow', label: t('settingsTheme.speedSlow', 'Slow') },
              { value: 'fast', label: t('settingsTheme.speedFast', 'Fast') },
            ]}
            value={defaultAnimateSpeed}
            onChange={(e) => setDefaultAnimateSpeed(e.target.value)}
          />
        </div>
      </section>

      {/* Custom CSS */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTheme.customCss')}</h2>
        <Textarea
          id="customCss"
          rows={6}
          placeholder=".storefront-header { ... }"
          className="font-mono text-sm"
          value={customCss}
          onChange={(e) => setCustomCss(e.target.value)}
        />
      </section>

      <div className="flex justify-end pb-6">
        <Button onClick={handleSave} isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
